/**
 * Next Story Bags — right-side off-canvas navigation drawer.
 *
 * <nsb-drawer> wraps the header trigger and the drawer markup. On connect the
 * drawer root is portaled to <body> so it escapes the sticky header's stacking
 * context. Closing paths: overlay click, close button, Esc, swipe right.
 * Accordions are single-open and animate via CSS grid rows.
 */
if (!customElements.get('nsb-drawer')) {
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

  const normalize = (str) =>
    (str || '')
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/&/g, 'and')
      .toLowerCase()
      .trim();

  class NsbDrawer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        // element was moved in the DOM — just re-attach the portal
        if (this.root && !this.root.isConnected) document.body.appendChild(this.root);
        return;
      }
      this.toggleButton = this.querySelector('[data-nsb-drawer-open]');
      this.root = this.querySelector('[data-nsb-drawer-root]');
      if (!this.toggleButton || !this.root) return;
      this.initialized = true;

      // Portal: move the drawer to <body>, replacing any stale copy left by a
      // theme-editor section re-render.
      const stale = document.getElementById(this.root.id);
      if (stale && stale !== this.root) stale.remove();
      document.body.appendChild(this.root);

      this.panel = this.root.querySelector('[data-nsb-drawer-panel]');
      this.isOpen = false;

      this.onToggleClick = () => (this.isOpen ? this.close() : this.open());
      this.onRootClick = (event) => {
        if (event.target.closest('[data-nsb-drawer-close]')) this.close();
      };
      this.onKeydown = this.onKeydown.bind(this);

      this.toggleButton.addEventListener('click', this.onToggleClick);
      this.root.addEventListener('click', this.onRootClick);

      this.initAccordions();
      this.initDesignerSearch();
      this.initSwipe();
    }

    disconnectedCallback() {
      if (this.isOpen) this.close({ restoreFocus: false });
      document.documentElement.classList.remove('nsb-scroll-lock');
      this.root?.remove();
    }

    /* ---------------- open / close ---------------- */
    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.lastFocused = document.activeElement;

      const gap = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty('--nsb-scrollbar-gap', `${gap}px`);
      document.documentElement.classList.add('nsb-scroll-lock');

      this.root.removeAttribute('inert');
      // next frame so the transition runs from the off-canvas state
      requestAnimationFrame(() => {
        this.root.classList.add('is-open');
        this.toggleButton.setAttribute('aria-expanded', 'true');
      });

      document.addEventListener('keydown', this.onKeydown);
      setTimeout(() => this.root.querySelector('.nsb-drawer__close')?.focus({ preventScroll: true }), 60);
    }

    close({ restoreFocus = true } = {}) {
      if (!this.isOpen) return;
      this.isOpen = false;

      this.root.classList.remove('is-open');
      this.toggleButton.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', this.onKeydown);

      const finish = () => {
        if (this.isOpen) return;
        this.root.setAttribute('inert', '');
        document.documentElement.classList.remove('nsb-scroll-lock');
        document.documentElement.style.removeProperty('--nsb-scrollbar-gap');
      };
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      reduced ? finish() : setTimeout(finish, 500);

      if (restoreFocus) (this.lastFocused || this.toggleButton)?.focus({ preventScroll: true });
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      // focus trap
      const nodes = [...this.panel.querySelectorAll(FOCUSABLE)].filter(
        (el) => el.offsetParent !== null && !el.closest('[inert]')
      );
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ---------------- accordions ---------------- */
    initAccordions() {
      this.accordions = [...this.root.querySelectorAll('[data-nsb-accordion]')];
      this.accordions.forEach((acc) => {
        const trigger = acc.querySelector('.nsb-acc__trigger');
        trigger.addEventListener('click', () => {
          const willOpen = trigger.getAttribute('aria-expanded') !== 'true';
          this.accordions.forEach((other) => other !== acc && this.setAccordion(other, false));
          this.setAccordion(acc, willOpen);
          if (willOpen) {
            const search = acc.querySelector('[data-nsb-designer-filter]');
            // Only auto-focus search on pointer devices; avoids popping the iOS keyboard.
            if (search && window.matchMedia('(hover: hover)').matches) {
              setTimeout(() => search.focus({ preventScroll: true }), 220);
            }
          }
        });
      });
    }

    setAccordion(acc, open) {
      const trigger = acc.querySelector('.nsb-acc__trigger');
      const panel = acc.querySelector('.nsb-acc__panel');
      trigger.setAttribute('aria-expanded', String(open));
      acc.classList.toggle('is-open', open);
      open ? panel.removeAttribute('inert') : panel.setAttribute('inert', '');
    }

    /* ---------------- designer search ---------------- */
    initDesignerSearch() {
      const input = this.root.querySelector('[data-nsb-designer-filter]');
      const list = this.root.querySelector('[data-nsb-designer-list]');
      if (!input || !list) return;

      const items = [...list.querySelectorAll('li[data-name]')].map((li) => ({
        li,
        key: normalize(li.dataset.name),
      }));
      const empty = this.root.querySelector('[data-nsb-designer-empty]');
      const emptyText = this.root.querySelector('[data-nsb-designer-empty-text]');
      const searchLink = this.root.querySelector('[data-nsb-designer-search-link]');

      const run = () => {
        const raw = input.value.trim();
        const q = normalize(raw);
        let shown = 0;
        items.forEach(({ li, key }) => {
          // accent/ampersand-insensitive substring match ("hermes" finds "Hermès")
          const match = !q || key.includes(q);
          li.hidden = !match;
          if (match) shown++;
        });
        if (empty) {
          empty.hidden = shown > 0 || !q;
          if (emptyText) emptyText.textContent = `No designer matches “${raw}”. `;
          if (searchLink) searchLink.href = `${searchLink.dataset.base}${encodeURIComponent(raw)}`;
        }
      };

      input.addEventListener('input', run);
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          const firstVisible = items.find(({ li }) => !li.hidden);
          if (firstVisible && input.value.trim()) {
            window.location.href = firstVisible.li.querySelector('a').href;
          } else if (searchLink && input.value.trim()) {
            window.location.href = searchLink.href;
          }
        }
      });
    }

    /* ---------------- swipe-to-close (touch) ---------------- */
    initSwipe() {
      let startX = 0;
      let startY = 0;
      let dx = 0;
      let tracking = false;

      this.panel.addEventListener(
        'touchstart',
        (e) => {
          if (e.touches.length !== 1) return;
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          dx = 0;
          tracking = true;
        },
        { passive: true }
      );

      this.panel.addEventListener(
        'touchmove',
        (e) => {
          if (!tracking) return;
          dx = e.touches[0].clientX - startX;
          const dy = Math.abs(e.touches[0].clientY - startY);
          if (dy > Math.abs(dx) || dx < 0) {
            // vertical scroll or leftward drag: let it be
            if (this.root.classList.contains('is-dragging')) {
              this.root.classList.remove('is-dragging');
              this.panel.style.transform = '';
            }
            return;
          }
          this.root.classList.add('is-dragging');
          this.panel.style.transform = `translateX(${dx}px)`;
        },
        { passive: true }
      );

      const end = () => {
        if (!tracking) return;
        tracking = false;
        const dragged = this.root.classList.contains('is-dragging');
        this.root.classList.remove('is-dragging');
        this.panel.style.transform = '';
        if (dragged && dx > Math.min(110, this.panel.offsetWidth * 0.3)) this.close();
      };
      this.panel.addEventListener('touchend', end);
      this.panel.addEventListener('touchcancel', end);
    }
  }

  customElements.define('nsb-drawer', NsbDrawer);
}
